import { useEffect, useState, type ReactNode } from 'react';

import {
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import FinancialAssessment from '@/karehero-app/care/forms/FinancialAssessment';
import { financialAssessmentSceneById } from '@/karehero-app/care/forms/FinancialAssessment.scenes';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';
import { conversationPrinciples } from '@/karehero-app/tell-us-once/intake/principles.content';
import {
  onboardingWalkthrough as content,
  type WalkthroughSlide,
} from '@/karehero-app/tell-us-once/walkthrough/OnboardingWalkthrough.content';

/**
 * Onboarding for a wider group than the team, presented: a slide per question
 * people ask of it, with the prototype live beside them. It opens on a
 * reminder of what Tell Us Once is for, with a form as it is today in the
 * phone, then what has changed.
 *
 * Next and Back move a slide at a time, and the phone jumps to the moment
 * each slide is about. The arrow keys do the same, except while typing in the
 * phone.
 *
 * The phone is Onboarding 2.0's dev concept, the same one the prototype opens
 * on, so it shows whatever the prototype does now, and can still be clicked
 * through on any slide.
 */
// Opens the research panel on one source: given its number across the deck.
type Cite = (n: number) => void;

/**
 * A point's text with its `[n]` markers drawn as citations. `n` counts the
 * slide's own research from 1; the number shown counts across the deck, so
 * it matches the panel.
 */
const Cited = ({
  text,
  offset,
  onCite,
}: {
  text: string;
  offset: number;
  onCite: Cite;
}) =>
  text.split(/\s*\[(\d+)\]/).map((part, i) => {
    // Split with a capture group: odd parts are the numbers.
    if (i % 2 === 0) return <Bolded key={i} text={part} />;
    const n = offset + Number(part);
    return (
      <Link
        key={i}
        component='button'
        type='button'
        onClick={() => onCite(n)}
        sx={{
          fontFamily: 'inherit',
          verticalAlign: 'super',
          fontSize: '0.65em',
          ml: 0.5,
        }}
      >
        {`[${n}]`}
      </Link>
    );
  });

/** A point's `**words**` drawn bold: the part to say out loud. */
export const Bolded = ({ text }: { text: string }) => (
  <>
    {text.split(/\*\*(.+?)\*\*/).map((part, i) =>
      i % 2 ? (
        <Box key={i} component='strong' sx={{ fontWeight: 600 }}>
          {part}
        </Box>
      ) : (
        part
      ),
    )}
  </>
);

// One point is an example rather than a list, so it reads as plain text.
const Points = ({
  points,
  offset,
  onCite,
}: {
  points: string[];
  offset: number;
  onCite: Cite;
}) =>
  points.length === 1 ? (
    <Typography variant='body1'>
      <Cited text={points[0]} offset={offset} onCite={onCite} />
    </Typography>
  ) : (
    <Stack component='ol' spacing={3} sx={{ m: 0, pl: 3 }}>
      {points.map((point) => (
        <Typography key={point} component='li' variant='body1'>
          <Cited text={point} offset={offset} onCite={onCite} />
        </Typography>
      ))}
    </Stack>
  );

type Source = NonNullable<WalkthroughSlide['research']>[number];

const OnboardingWalkthrough = ({
  slides: given,
  screens,
}: {
  /** The deck to present. The onboarding walkthrough unless given another. */
  slides?: WalkthroughSlide[];
  /** The screens another deck's slides name with `screen`, for the phone. */
  screens?: Record<string, () => ReactNode>;
}) => {
  // Typed as slides, so a field only some slides have (link) can be read on
  // any of them.
  const deck: WalkthroughSlide[] = given ?? content.slides;
  const slides = deck.filter((s) => !s.hidden);
  // Research no slide on show cites, and the principles, under the cited
  // sources. Only the onboarding walkthrough's own; another deck has none.
  const more: Source[] = given
    ? []
    : [
        ...deck.filter((s) => s.hidden).flatMap((s) => s.research ?? []),
        ...content.moreResearch,
      ];
  const principles = given ? [] : conversationPrinciples;
  const [slide, setSlide] = useState(0);
  // The source the research panel is open on, 0 when it is open on none, or
  // null while it's shut.
  const [cited, setCited] = useState<number | null>(null);
  // The principles at the foot of the research panel, shut until opened.
  const [principlesOpen, setPrinciplesOpen] = useState(false);

  // Where each slide's research starts in the deck's numbering.
  const offsets = slides.map((_, i) =>
    slides
      .slice(0, i)
      .reduce((total, s) => total + (s.research?.length ?? 0), 0),
  );

  const current = slides[slide];
  const offset = offsets[slide];
  const { form } = current;
  const atStart = slide === 0;
  const atEnd = slide === slides.length - 1;

  const next = () => setSlide((n) => Math.min(n + 1, slides.length - 1));
  const back = () => setSlide((n) => Math.max(n - 1, 0));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('input, textarea, [contenteditable="true"]')) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') next();
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // How many sources the slides on show cite: the rest number on from here.
  const cites = slides.reduce((t, s) => t + (s.research?.length ?? 0), 0);

  // One source in the panel, picked out when it is the one cited. Given the
  // slide that cites it, it links back there.
  const row = (
    n: number,
    { note, source, href }: Source,
    slideIndex?: number,
  ) => {
    const picked = n === cited;
    return (
      <Stack
        key={n}
        direction='row'
        spacing={2}
        // Brings the one clicked into view.
        ref={(el: HTMLDivElement | null) => {
          if (el && picked) el.scrollIntoView({ block: 'nearest' });
        }}
        // Picked by clicking it here too, not only from a citation,
        // and by keyboard: a row of its own in the tab order.
        role='button'
        tabIndex={0}
        aria-pressed={picked}
        onClick={() => setCited(n)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCited(n);
          }
        }}
        // All in light grey; the one clicked darkens on a faint
        // tint, rather than sitting in a card.
        sx={{
          p: 2,
          borderRadius: 1,
          cursor: 'pointer',
          color: picked ? 'text.primary' : 'text.secondary',
          bgcolor: picked ? 'custom.neutral.100' : 'transparent',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: 'inherit', flexShrink: 0 }}
        >{`[${n}]`}</Typography>
        <Typography variant='body2' sx={{ color: 'inherit' }}>
          {`${note} `}
          {href ? (
            <Link
              href={href}
              target='_blank'
              rel='noreferrer'
              sx={{ color: 'inherit' }}
            >
              {source}
            </Link>
          ) : (
            source
          )}{' '}
          {slideIndex !== undefined && (
            <>
              {/* Goes to the slide that cites it, with the list kept
                  open on this source. */}
              (
              <Link
                component='button'
                type='button'
                onClick={() => setSlide(slideIndex)}
                sx={{
                  font: 'inherit',
                  color: 'inherit',
                  verticalAlign: 'baseline',
                }}
              >
                {`Slide ${slideIndex + 1}`}
              </Link>
              )
            </>
          )}
        </Typography>
      </Stack>
    );
  };

  const nav = (
    <Stack
      direction='row'
      spacing={2}
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Stack direction='row' spacing={2} sx={{ alignItems: 'baseline' }}>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {`${slide + 1} of ${slides.length}`}
        </Typography>
        {/* Opens the panel without a citation, for what no slide cites.
            Only for a deck that has research. */}
        {cites + more.length > 0 && (
          <Link
            component='button'
            type='button'
            variant='caption'
            onClick={() => setCited(cited === null ? 0 : null)}
          >
            Research
          </Link>
        )}
      </Stack>
      <Stack direction='row' spacing={2}>
        <Button
          variant='secondary'
          size='sm'
          ariaLabel='Back'
          isDisabled={atStart}
          onPress={back}
        >
          Back
        </Button>
        <Button
          variant='primary'
          size='sm'
          ariaLabel='Next'
          isDisabled={atEnd}
          onPress={next}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Stack
      direction='row'
      spacing={10}
      sx={{
        height: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'background.default',
        p: 6,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {form && (
        <Box sx={{ flexShrink: 0 }}>
          <PhoneFrame>
            <FinancialAssessment
              key={slide}
              {...financialAssessmentSceneById(form).initial}
              showSection={false}
            />
          </PhoneFrame>
        </Box>
      )}

      {/* Several phones, smaller, each with its caption under it. */}
      {current.frames && (
        <Stack
          direction='row'
          spacing={4}
          sx={{ alignItems: 'flex-start', justifyContent: 'center' }}
        >
          {current.frames.map(({ frame, label }) => (
            <Stack key={frame} spacing={2} sx={{ alignItems: 'center' }}>
              {/* As big as five can go across a laptop screen. */}
              <PhoneFrame scale={0.62}>
                <Flow
                  key={`${slide}-${frame}`}
                  initial={frame}
                  option={2}
                  version={current.version ?? '2.0-dev'}
                />
              </PhoneFrame>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {current.screenRow && screens && (
        <Stack
          direction='row'
          spacing={4}
          sx={{ alignItems: 'flex-start', flexShrink: 0 }}
        >
          {current.screenRow.map(({ screen, label }) => (
            <Stack key={screen} spacing={2} sx={{ alignItems: 'center' }}>
              <PhoneFrame key={`${slide}-${screen}`} scale={0.85}>
                {screens[screen]?.()}
              </PhoneFrame>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {current.screen && screens?.[current.screen] && (
        <Box sx={{ flexShrink: 0 }}>
          {/* A new mount each slide, so the phone starts on its moment. */}
          <PhoneFrame key={slide}>{screens[current.screen]()}</PhoneFrame>
        </Box>
      )}

      {current.frame && (
        <Box sx={{ flexShrink: 0 }}>
          <PhoneFrame>
            {/* A new mount each slide, so the phone starts on its moment. */}
            <Flow
              key={slide}
              initial={current.frame}
              option={2}
              version={current.version ?? '2.0-dev'}
            />
          </PhoneFrame>
        </Box>
      )}

      {current.question && (
        <Stack
          sx={{ width: 560, height: '100%', justifyContent: 'space-between' }}
        >
          <Stack
            key={slide}
            spacing={4}
            sx={{
              flex: 1,
              justifyContent: 'center',
              '@keyframes slideIn': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'none' },
              },
              '& > *': { animation: 'slideIn .3s ease both' },
            }}
          >
            {/* The first slide sets the scene, so its title reads a size up. */}
            <Typography variant={atStart ? 'h1' : 'h2'}>
              {current.question}
            </Typography>
            {current.intro && (
              <Typography variant='body1'>
                <Bolded text={current.intro} />
              </Typography>
            )}
            {current.points && (
              <Points
                points={current.points}
                offset={offset}
                onCite={setCited}
              />
            )}
            {/* Groups sit further apart than the rest, so each reads as its
                own section. */}
            {current.groups && (
              <Stack spacing={7}>
                {current.groups.map((group) => (
                  <Stack key={group.heading} spacing={2}>
                    <Typography variant='h4'>{group.heading}</Typography>
                    <Points
                      points={group.points}
                      offset={offset}
                      onCite={setCited}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
            {current.footnote && (
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {current.footnote}
              </Typography>
            )}
            {current.link && (
              <Box>
                <Button
                  variant='secondary'
                  size='sm'
                  ariaLabel={current.link.label}
                  to={current.link.href}
                >
                  {current.link.label}
                </Button>
              </Box>
            )}
          </Stack>
        </Stack>
      )}

      {/* Pinned to the bottom middle on every slide, with or without a phone
          or text, so Back and Next never move and can be clicked through fast. */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: 48,
          width: 560,
        }}
      >
        {nav}
      </Box>

      {/* Every source in the deck as one numbered list down the right, opened
          from a citation with the one clicked picked out. Over the slide so
          nothing on it moves; only there while open, so it can be wide. */}
      {cited !== null && (
        <Stack
          spacing={3}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 560,
            p: 5,
            overflowY: 'auto',
            bgcolor: 'background.default',
            borderLeft: 1,
            borderColor: 'custom.neutral.200',
          }}
        >
          <Stack
            direction='row'
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant='h5'>Research</Typography>
            <IconButton
              aria-label='Close research'
              size='small'
              onClick={() => setCited(null)}
            >
              <Icon icon={'xmark' as never} fontSize={13} />
            </IconButton>
          </Stack>
          <Stack spacing={1}>
            {slides.flatMap((s, i) =>
              (s.research ?? []).map((r, j) => row(offsets[i] + j + 1, r, i)),
            )}
          </Stack>
          {more.length > 0 && (
            <Stack spacing={1}>
              {more.map((r, j) => row(cites + j + 1, r))}
            </Stack>
          )}
          {/* Shut until opened: the sources are what the panel is for. */}
          {principles.length > 0 && (
            <Stack spacing={2} sx={{ px: 2 }}>
              <ButtonBase
                onClick={() => setPrinciplesOpen((o) => !o)}
                aria-expanded={principlesOpen}
                sx={{ justifyContent: 'space-between', borderRadius: 1 }}
              >
                <Typography variant='h6'>
                  Conversation design principles
                </Typography>
                <Icon
                  icon={
                    (principlesOpen ? 'chevron-up' : 'chevron-down') as never
                  }
                  fontSize={12}
                />
              </ButtonBase>
              <Collapse in={principlesOpen}>
                <Stack spacing={2}>
                  {principles.map((p) => (
                    <Typography key={p.name} variant='body2'>
                      <b>{p.name}</b> {p.text}
                    </Typography>
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default OnboardingWalkthrough;
